import { type ComponentType, type RefAttributes } from "react";
import { NavLink } from "react-router-dom";
import { Collapsible } from "@base-ui/react/collapsible";
import { NavigationMenu as BaseNavigationMenu } from "@base-ui/react/navigation-menu";
import {
  IconAddressBook,
  IconChartBar,
  IconCreditCard,
  IconFolder,
  IconGift,
  IconHome,
  IconLayoutDashboard,
  IconLifebuoy,
  IconList,
  IconLock,
  IconMenu2,
  type IconProps,
  IconReceipt,
  IconScale,
  IconUser,
  IconUserPlus,
  IconUsers,
} from "@tabler/icons-react";
import styles from "./NavigationMenu.module.css";
// @ts-expect-error pure js
import { classnames } from "../../../utils";

import { useNavigationMenu } from "./use-navigation-menu.ts";
import ProfileFilterSelect from "../Layout/ProfileFilterSelect.tsx";
import Tooltip from "../Tooltip";
import useOnboarding from "../../../hooks/use-onboarding.ts";
// @ts-expect-error pure js
import useAuth from "../../../hooks/use-auth.js";
import type { OnboardingMilestone } from "../../../models/onboarding";

type NavRequirement = OnboardingMilestone;

type NavItem = {
  label: string;
  icon: ComponentType<IconProps & RefAttributes<SVGSVGElement>>;
  end?: boolean;
  /** Jalon d'onboarding requis pour déverrouiller l'entrée (gating nouveaux comptes). */
  requires?: NavRequirement;
  /** Entrée masquée pour les comptes mandataires (home "/pro"). */
  hiddenForPro?: boolean;
  /** Groupe secondaire, ancré en bas de la sidebar. Set only on first item of secondary group. */
  secondary?: boolean;
  /** Entrée réservée aux comptes mandataires (home "/pro"). */
  proOnly?: boolean;
} & (
  | { to: string; href?: never }
  /** Lien externe, ouvert dans un nouvel onglet. */
  | { href: string; to?: never }
);

const LOCKED_MESSAGES: Record<NavRequirement, string> = {
  profile: "Créez d'abord votre profil bailleur pour débloquer cette section",
  property: "Ajoutez votre premier bien pour débloquer cette section",
};

interface NavigationMenuProps {
  isBailleur: boolean;
  isTenant: boolean;
}

const bailleurNavItems: NavItem[] = [
  {
    label: "Tableau de Bord",
    icon: IconLayoutDashboard,
    to: "/bailleur",
    end: true,
  },
  { label: "Bailleurs", icon: IconUser, to: "/bailleur/profiles" },
  {
    label: "Biens",
    icon: IconHome,
    to: "/bailleur/properties",
    requires: "profile",
  },
  {
    label: "Locataires",
    icon: IconUsers,
    to: "/bailleur/tenants",
    requires: "property",
  },
  {
    label: "Candidats",
    icon: IconUserPlus,
    to: "/bailleur/candidats",
    requires: "property",
  },
  {
    label: "Pilotage",
    icon: IconChartBar,
    to: "/bailleur/pilotage",
    requires: "property",
  },
  {
    label: "Banques",
    icon: IconCreditCard,
    to: "/bailleur/finances",
    requires: "property",
  },
  {
    label: "Fiscalité",
    icon: IconReceipt,
    to: "/bailleur/fiscalite",
    requires: "property",
  },
  {
    label: "Comptabilité",
    icon: IconScale,
    to: "/bailleur/compta",
    proOnly: true,
  },
  {
    label: "Carnet d'adresse",
    icon: IconAddressBook,
    to: "/bailleur/carnet-adresse",
    requires: "property",
  },
  {
    label: "Parrainage",
    icon: IconGift,
    to: "/bailleur/parrainage",
    hiddenForPro: true,
    secondary: true,
  },
  {
    label: "Centre d'aide",
    icon: IconLifebuoy,
    href: "https://help.qalimo.fr",
  },
];

const tenantNavItems: NavItem[] = [
  {
    label: "Tableau de Bord",
    icon: IconLayoutDashboard,
    to: "/locataire",
    end: true,
  },
  {
    label: "Dossiers de candidature",
    icon: IconFolder,
    to: "/candidat",
    end: true,
  },
  {
    label: "Liste des candidatures",
    icon: IconList,
    to: "/locataire/candidatures",
    end: true,
  },
  {
    label: "Centre d'aide",
    icon: IconLifebuoy,
    href: "https://help.qalimo.fr",
    secondary: true,
  },
];

export function NavigationMenu({ isBailleur, isTenant }: NavigationMenuProps) {
  const { collapsed, toggleCollapsed, closeMobile } = useNavigationMenu();
  const { gating, onboarding } = useOnboarding();
  const { isPro } = useAuth();

  const navItems: NavItem[] = (
    isBailleur ? bailleurNavItems : isTenant ? tenantNavItems : []
  ).filter((item) => (isPro ? !item.hiddenForPro : !item.proOnly));

  // Jalons stockés dans onboarding : posés au premier franchissement, ils ne
  // re-verrouillent jamais un menu après une suppression.
  const milestones: Record<NavRequirement, boolean> = {
    profile: onboarding.reached_milestones.includes("profile"),
    property: onboarding.reached_milestones.includes("property"),
  };

  function isLocked(item: NavItem) {
    return (
      isBailleur && gating && !!item.requires && !milestones[item.requires]
    );
  }

  return (
    <Collapsible.Root
      className={styles.sidebar}
      open={!collapsed}
      onOpenChange={toggleCollapsed}
    >
      <Collapsible.Trigger
        render={<div />}
        nativeButton={false}
        tabIndex={-1}
        className={styles.backdrop}
      />
      <div className={styles.menuButtonWrapper}>
        <Collapsible.Trigger className={styles.menuButton}>
          <IconMenu2 />
        </Collapsible.Trigger>
      </div>
      <Collapsible.Panel keepMounted className={styles.sidebarPanel}>
        {isBailleur && (
          <div className={styles.profileFilter}>
            <ProfileFilterSelect />
          </div>
        )}
        <BaseNavigationMenu.Root
          orientation={"vertical"}
          className={styles.nav}
        >
          <BaseNavigationMenu.List>
            {navItems.map((item, i) => {
              const { label, icon: MenuIcon, end } = item;
              const locked = isLocked(item);
              const secondaryStart = item.secondary && styles.secondaryStart;

              if (locked) {
                return (
                  <BaseNavigationMenu.Item
                    key={i}
                    className={classnames(styles.lockedItem, secondaryStart)}
                  >
                    <Tooltip
                      content={LOCKED_MESSAGES[item.requires as NavRequirement]}
                      direction="right"
                    >
                      <div
                        className={classnames(styles.navLink, styles.locked)}
                        aria-disabled="true"
                      >
                        <MenuIcon />
                        <span>{label}</span>
                        <IconLock size={14} className={styles.lockIcon} />
                      </div>
                    </Tooltip>
                  </BaseNavigationMenu.Item>
                );
              }

              if (item.href) {
                return (
                  <BaseNavigationMenu.Item
                    key={i}
                    className={classnames(secondaryStart)}
                  >
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={closeMobile}
                      className={styles.navLink}
                    >
                      <MenuIcon />
                      <span>{label}</span>
                    </a>
                  </BaseNavigationMenu.Item>
                );
              }

              return (
                <BaseNavigationMenu.Item
                  key={i}
                  className={classnames(secondaryStart)}
                >
                  <NavLink
                    to={item.to!}
                    end={end}
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      classnames(styles.navLink, isActive && styles.active)
                    }
                  >
                    <MenuIcon />
                    <span>{label}</span>
                  </NavLink>
                </BaseNavigationMenu.Item>
              );
            })}
          </BaseNavigationMenu.List>
        </BaseNavigationMenu.Root>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}
